/**
 * The borrowers this template covers, and the inline form that adds one.
 *
 * The form itself lives in `AddBorrowerForm` since DOP-455 — the Periods board
 * renders the same one — and the name it takes is a pick from the user's own
 * filenames, not free text (DOP-454): the package matcher reads the filename
 * prefix, so a spelling the corpus does not use is a borrower whose package
 * never arrives.
 *
 * Inline rather than a modal — adding eight borrowers in a row through a dialog
 * that closes each time is eight extra clicks, and nothing here needs the focus
 * trap a modal buys.
 */

import { Plus } from "lucide-react";
import React, { useState } from "react";
import type {
	MonitoringBorrower,
	MonitoringBorrowerCandidateGroup,
	MonitoringCreateBorrowerPayload,
} from "../../types/monitoring";
import AddBorrowerForm, {
	type BorrowerCreateResult,
} from "./AddBorrowerForm";

export type { BorrowerCreateResult };

interface BorrowerListProps {
	borrowers: MonitoringBorrower[];
	/** The names the user's documents carry, for the picker. */
	candidates: MonitoringBorrowerCandidateGroup[];
	/** Must not reject; a failure comes back as `{ ok: false }`. */
	onCreate: (
		payload: MonitoringCreateBorrowerPayload,
	) => Promise<BorrowerCreateResult>;
}

const BorrowerList: React.FC<BorrowerListProps> = ({
	borrowers,
	candidates,
	onCreate,
}) => {
	const [open, setOpen] = useState(false);

	return (
		<section>
			<div className="flex items-center justify-between">
				<p className="text-sm text-[#5B6472]">The borrowers this workflow reads a package for each period.</p>
				<button
					type="button"
					onClick={() => setOpen((current) => !current)}
					aria-expanded={open}
					className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#004C4B] px-3.5 text-sm font-semibold text-white hover:bg-[#003a3a]"
				>
					<Plus className="h-3.5 w-3.5" aria-hidden="true" />
					Add borrower
				</button>
			</div>

			{open && (
				<div className="mt-3">
					<AddBorrowerForm
						candidates={candidates}
						onCreate={onCreate}
						// `onDone`, NOT `onAdded`: a bulk add that only partly lands
						// has still changed the roster, and closing on that would
						// unmount the form before it can say which names were
						// refused. The roster itself is refetched by the page's
						// `onCreate`, so this host needs no `onAdded`.
						onDone={() => setOpen(false)}
						onCancel={() => setOpen(false)}
					/>
				</div>
			)}

			{borrowers.length === 0 ? (
				<p className="mt-4 rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-text-muted">
					No borrowers yet.
				</p>
			) : (
				<ul className="mt-4 divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
					{borrowers.map((borrower) => (
						<li key={borrower.public_id} className="px-4 py-3">
							<span className="text-[14px] font-semibold text-[#111111]">{borrower.name}</span>
							<span className="mt-0.5 block text-sm text-text-muted">
								{[borrower.sponsor, borrower.sector].filter(Boolean).join(" · ") ||
									"No sponsor or sector"}
							</span>
							{borrower.aliases.length > 0 && (
								<span className="mt-0.5 block text-xs text-text-muted">
									Also known as {borrower.aliases.join(", ")}
								</span>
							)}
						</li>
					))}
				</ul>
			)}
		</section>
	);
};

export default BorrowerList;
